import { createBrowserRouter, Navigate } from 'react-router';

import { AppShell } from './layout/AppShell';
import { NotFoundPage } from './pages/AppPages';
import { ChartTabSoon } from './patients/chart/ChartTabSoon';

import type { ComponentType } from 'react';

/**
 * Pantalla con carga diferida (RNF-PERF-05): cada ruta es su propio chunk y el bundle inicial
 * lleva solo el shell. React Router espera el import antes de mostrarla.
 */
function page<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M & string) {
  return async () => ({ Component: (await load())[name] as ComponentType });
}

/** Rutas de docs/03 › Web (slugs en inglés). Las secciones sin pantalla muestran un vacío. */
export const routes = [
  { path: '/login', lazy: page(() => import('./pages/LoginPage'), 'LoginPage') },
  { path: '/forgot-password', lazy: page(() => import('./pages/PasswordPages'), 'ForgotPasswordPage') },
  { path: '/reset-password', lazy: page(() => import('./pages/PasswordPages'), 'ResetPasswordPage') },
  { path: '/invite/:token', lazy: page(() => import('./pages/PasswordPages'), 'InvitePage') },
  // RF-PLAT-56: confirmar el email nuevo, sin sesión.
  { path: '/confirm-email', lazy: page(() => import('./pages/PasswordPages'), 'ConfirmEmailPage') },
  // FB-29: el paciente completa el formulario de ingreso sin sesión (o en el iPad de recepción).
  { path: '/intake/:token', lazy: page(() => import('./patients/intake/IntakePage'), 'IntakePage') },
  { path: '/select-clinic', lazy: page(() => import('./pages/SelectClinicPage'), 'SelectClinicPage') },
  // RF-AUTH-04, RNF-PLAT-01: activar el 2FA obligatorio, con o sin clínica.
  { path: '/two-factor', lazy: page(() => import('./pages/TwoFactorPage'), 'TwoFactorPage') },
  {
    // Consola de plataforma (6.1b, brief §4): layout propio, solo el administrador de plataforma.
    path: '/platform',
    lazy: page(() => import('./platform/PlatformShell'), 'PlatformShell'),
    children: [
      { index: true, lazy: page(() => import('./platform/OverviewPage'), 'OverviewPage') },
      { path: 'clinics', lazy: page(() => import('./platform/ClinicsPage'), 'ClinicsPage') },
      { path: 'clinics/new', lazy: page(() => import('./platform/NewClinicPage'), 'NewClinicPage') },
      {
        path: 'clinics/:id',
        lazy: page(() => import('./platform/ClinicDetailPage'), 'ClinicDetailPage'),
        children: [
          { index: true, lazy: page(() => import('./platform/ClinicDetailPage'), 'ClinicSummaryTab') },
          { path: 'users', lazy: page(() => import('./platform/ClinicDetailPage'), 'ClinicUsersTab') },
          {
            path: 'lifecycle',
            lazy: page(() => import('./platform/ClinicLifecycleTab'), 'ClinicLifecycleTab'),
          },
        ],
      },
      { path: 'users', lazy: page(() => import('./platform/UsersPage'), 'UsersPage') },
      { path: 'users/:id', lazy: page(() => import('./platform/UserDetailPage'), 'UserDetailPage') },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to="/my-day" replace /> },
      { path: '/my-day', lazy: page(() => import('./my-day/MyDayPage'), 'MyDayPage') },
      { path: '/agenda', lazy: page(() => import('./agenda/AgendaPage'), 'AgendaPage') },
      {
        path: '/agenda/aligner-changes',
        lazy: page(() => import('./aligners/AlignerChangesPage'), 'AlignerChangesPage'),
      },
      { path: '/patients', lazy: page(() => import('./patients/PatientsPage'), 'PatientsPage') },
      { path: '/patients/contacts', lazy: page(() => import('./patients/ContactsPage'), 'ContactsPage') },
      {
        path: '/patients/intake-forms',
        lazy: page(() => import('./patients/intake/IntakeInboxPage'), 'IntakeInboxPage'),
      },
      {
        // Ficha (RF-PAC-04): encabezado y pestañas; cada pestaña llega con su tarea.
        path: '/patients/:id',
        lazy: page(() => import('./patients/chart/PatientChart'), 'PatientChart'),
        children: [
          { index: true, lazy: page(() => import('./patients/chart/OverviewTab'), 'OverviewTab') },
          { path: 'edit', lazy: page(() => import('./patients/PatientEditPage'), 'PatientEditPage') },
          {
            path: 'medical-history',
            lazy: page(() => import('./patients/chart/MedicalHistoryTab'), 'MedicalHistoryTab'),
          },
          {
            path: 'admin-notes',
            lazy: page(() => import('./patients/chart/AdminNotesTab'), 'AdminNotesTab'),
          },
          {
            path: 'appointments',
            lazy: page(() => import('./patients/chart/AppointmentsTab'), 'AppointmentsTab'),
          },
          { path: 'documents', lazy: page(() => import('./patients/chart/DocumentsTab'), 'DocumentsTab') },
          {
            path: 'tooth-chart',
            lazy: page(() => import('./patients/chart/ToothChartTab'), 'ToothChartTab'),
          },
          { path: 'perio', lazy: page(() => import('./perio/PerioTab'), 'PerioTab') },
          { path: 'notes', lazy: page(() => import('./patients/chart/TimelineTab'), 'TimelineTab') },
          {
            path: 'treatment-plans',
            lazy: page(() => import('./plans/TreatmentPlansTab'), 'TreatmentPlansTab'),
          },
          { path: 'aligners', lazy: page(() => import('./aligners/AlignersTab'), 'AlignersTab') },
          { path: 'statement', lazy: page(() => import('./statement/StatementTab'), 'StatementTab') },
          { path: '*', element: <ChartTabSoon /> },
        ],
      },
      { path: '/visit', lazy: page(() => import('./visit/VisitIndex'), 'VisitIndex') },
      { path: '/visit/:appointmentId', lazy: page(() => import('./visit/VisitPage'), 'VisitPage') },
      {
        path: '/billing',
        lazy: page(() => import('./billing/BillingLayout'), 'BillingLayout'),
        children: [
          { path: 'cash', lazy: page(() => import('./billing/CashPage'), 'CashPage') },
          { path: 'settlements', lazy: page(() => import('./billing/SettlementsPage'), 'SettlementsPage') },
          {
            path: 'adjustments',
            lazy: page(() => import('./billing/AdjustmentRequestsPage'), 'AdjustmentRequestsPage'),
          },
          { path: 'production', lazy: page(() => import('./billing/ProductionPage'), 'ProductionPage') },
          { path: '*', element: <ChartTabSoon /> },
        ],
      },
      { path: '/reports', element: <Navigate to="/reports/money" replace /> },
      { path: '/reports/:report', lazy: page(() => import('./reports/ReportsPage'), 'ReportsPage') },
      {
        path: '/operations',
        lazy: page(() => import('./operations/OperationsLayout'), 'OperationsLayout'),
        children: [
          { path: 'board', lazy: page(() => import('./operations/BoardPage'), 'BoardPage') },
          { path: 'journey', lazy: page(() => import('./operations/JourneyPage'), 'JourneyPage') },
          { path: 'team', lazy: page(() => import('./operations/TeamPage'), 'TeamPage') },
          { path: 'issues', lazy: page(() => import('./operations/IssuesPage'), 'IssuesPage') },
          { path: 'protocols', lazy: page(() => import('./operations/ProtocolsPage'), 'ProtocolsPage') },
          { path: 'meeting', lazy: page(() => import('./operations/MeetingPage'), 'MeetingPage') },
          { path: 'ask', lazy: page(() => import('./operations/AskPage'), 'AskPage') },
          { path: '*', element: <ChartTabSoon /> },
        ],
      },
      {
        path: '/inventory',
        lazy: page(() => import('./inventory/InventoryLayout'), 'InventoryLayout'),
        children: [
          { path: 'items', lazy: page(() => import('./inventory/ItemsPage'), 'ItemsPage') },
          { path: 'movements', lazy: page(() => import('./inventory/MovementsPage'), 'MovementsPage') },
          { path: 'warehouses', lazy: page(() => import('./inventory/CatalogPages'), 'WarehousesPage') },
          { path: 'suppliers', lazy: page(() => import('./inventory/CatalogPages'), 'SuppliersPage') },
        ],
      },
      {
        path: '/settings',
        lazy: page(() => import('./settings/SettingsLayout'), 'SettingsLayout'),
        children: [
          { path: 'providers', lazy: page(() => import('./settings/ProvidersSection'), 'ProvidersSection') },
          {
            path: 'appointment-types',
            lazy: page(() => import('./settings/AppointmentTypesSection'), 'AppointmentTypesSection'),
          },
          {
            path: 'contact-sources',
            lazy: page(() => import('./settings/ContactSourcesSection'), 'ContactSourcesSection'),
          },
          { path: 'templates', lazy: page(() => import('./settings/TemplatesSection'), 'TemplatesSection') },
          {
            path: 'prescriptions',
            lazy: page(
              () => import('./settings/PrescriptionTemplatesSection'),
              'PrescriptionTemplatesSection',
            ),
          },
          {
            path: 'procedures',
            lazy: page(() => import('./settings/ProceduresSection'), 'ProceduresSection'),
          },
          {
            path: 'payment-methods',
            lazy: page(() => import('./settings/PaymentMethodsSection'), 'PaymentMethodsSection'),
          },
          {
            path: 'integrations',
            lazy: page(() => import('./settings/integrations/IntegrationsPage'), 'IntegrationsPage'),
          },
          {
            path: 'integrations/:key',
            lazy: page(() => import('./settings/integrations/IntegrationPage'), 'IntegrationPage'),
          },
          { path: 'users', lazy: page(() => import('./settings/users/UsersPage'), 'UsersPage') },
          { path: 'branches', lazy: page(() => import('./settings/branches/BranchesPage'), 'BranchesPage') },
          { path: 'security', lazy: page(() => import('./settings/security/SecurityPage'), 'SecurityPage') },
          {
            path: 'appearance',
            lazy: page(() => import('./settings/appearance/AppearancePage'), 'AppearancePage'),
          },
          {
            path: 'documents',
            lazy: page(() => import('./settings/documents/DocumentsPage'), 'DocumentsPage'),
          },
          {
            path: 'imports',
            lazy: page(() => import('./settings/imports/ImportsSection'), 'ImportsSection'),
          },
          { path: 'audit', lazy: page(() => import('./settings/audit/AuditPage'), 'AuditPage') },
          { path: 'exports', lazy: page(() => import('./settings/exports/ExportsPage'), 'ExportsPage') },
          { path: '*', element: <ChartTabSoon /> },
        ],
      },
      { path: '/profile', lazy: page(() => import('./profile/ProfilePage'), 'ProfilePage') },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
];

export const createRouter = () => createBrowserRouter(routes);
